import {z} from 'zod'

import {toApiDateString} from '../jobs/job-schedule-format'
import {locationInputSchema, toLocationRequest} from '../jobs/location-input'
import {createProjectInputSchema, createProjectRequestSchema} from '../projects/schemas'

const scheduleRoleSchema = z.enum(['deploy', 'service', 'pickup'])
const jobStatusSchema = z.enum(['planned', 'active', 'completed', 'cancelled'])
const previewOutcomeSchema = z.enum(['created', 'updated', 'removed', 'unlinked', 'unchanged'])
const unchangedReasonSchema = z.enum(['worked', 'past', 'skipped', 'already_current', 'unaffected'])

const requirementRawSchema = z.object({
    type_id: z.string(),
    quantity: z.number(),
})

export const scheduleJobRawSchema = z.object({
    id: z.string(),
    schedule_role: scheduleRoleSchema.nullable(),
    name: z.string(),
    status: jobStatusSchema,
    display_id: z.string(),
    start_date: z.string(),
    notes: z.string().nullable(),
    po_number: z.string().nullable().optional(),
    project_billing_terms_id: z.string().optional(),
    worker_requirements: z.array(requirementRawSchema),
    asset_requirements: z.array(requirementRawSchema),
})

export const serviceScheduleRawSchema = z.object({
    id: z.string(),
    project_id: z.string(),
    service_type_id: z.string(),
    start_date: z.string(),
    end_date: z.string(),
    service_start_time: z.string().nullable().default(null),
    service_end_time: z.string().nullable().default(null),
    cadence_weekdays: z.array(z.number()),
    visits_only: z.boolean().optional(),
    notes: z.string().nullable(),
    po_number: z.string().nullable().optional(),
    project_billing_terms_id: z.string().optional(),
    worker_requirements: z.array(requirementRawSchema),
    asset_requirements: z.array(requirementRawSchema),
    created_at: z.string(),
    archived_at: z.string().nullable(),
    jobs: z.array(scheduleJobRawSchema),
})

export const previewRowRawSchema = z.object({
    job_id: z.string().nullable(),
    role: scheduleRoleSchema,
    start_date: z.string(),
    outcome: previewOutcomeSchema,
    reason: unchangedReasonSchema.nullable(),
})

export const changePreviewRawSchema = z.object({
    rows: z.array(previewRowRawSchema),
    created_count: z.number(),
    updated_count: z.number(),
    removed_count: z.number(),
    unlinked_count: z.number(),
    unchanged_count: z.number(),
    pickup_move_blocked: z.boolean(),
})

function mapRequirements(raw: z.infer<typeof requirementRawSchema>[]) {
    return raw.map((requirement) => ({
        typeId: requirement.type_id,
        quantity: requirement.quantity,
    }))
}

export const scheduleJobSchema = scheduleJobRawSchema.transform((raw) => ({
    id: raw.id,
    scheduleRole: raw.schedule_role,
    name: raw.name,
    status: raw.status,
    displayId: raw.display_id,
    startDate: raw.start_date,
    notes: raw.notes,
    poNumber: raw.po_number ?? null,
    projectBillingTermsId: raw.project_billing_terms_id ?? null,
    workerRequirements: mapRequirements(raw.worker_requirements),
    assetRequirements: mapRequirements(raw.asset_requirements),
}))

export const serviceScheduleSchema = serviceScheduleRawSchema.transform((raw) => ({
    id: raw.id,
    projectId: raw.project_id,
    serviceTypeId: raw.service_type_id,
    startDate: raw.start_date,
    endDate: raw.end_date,
    serviceStartTime: raw.service_start_time ?? null,
    serviceEndTime: raw.service_end_time ?? null,
    cadenceWeekdays: raw.cadence_weekdays,
    visitsOnly: raw.visits_only ?? false,
    notes: raw.notes,
    poNumber: raw.po_number ?? null,
    projectBillingTermsId: raw.project_billing_terms_id ?? null,
    workerRequirements: mapRequirements(raw.worker_requirements),
    assetRequirements: mapRequirements(raw.asset_requirements),
    createdAt: raw.created_at,
    archivedAt: raw.archived_at,
    jobs: raw.jobs.map((job) => scheduleJobSchema.parse(job)),
}))

export const previewRowSchema = previewRowRawSchema.transform((raw) => ({
    jobId: raw.job_id,
    role: raw.role,
    startDate: raw.start_date,
    outcome: raw.outcome,
    reason: raw.reason,
}))

export const changePreviewSchema = changePreviewRawSchema.transform((raw) => ({
    rows: raw.rows.map((row) => previewRowSchema.parse(row)),
    createdCount: raw.created_count,
    updatedCount: raw.updated_count,
    removedCount: raw.removed_count,
    unlinkedCount: raw.unlinked_count,
    unchangedCount: raw.unchanged_count,
    pickupMoveBlocked: raw.pickup_move_blocked,
}))

const stageInputSchema = z
    .object({
        workerRequirements: z
            .array(
                z.object({
                    workerTypeId: z.string(),
                    quantity: z.number().int().positive(),
                }),
            )
            .default([]),
        assetRequirements: z
            .array(
                z.object({
                    rentalAssetTypeId: z.string(),
                    quantity: z.number().int().positive(),
                }),
            )
            .default([]),
        notes: z.string().nullable().optional(),
        startTime: z.string().nullable().optional(),
        endTime: z.string().nullable().optional(),
    })
    .superRefine((stage, context) => {
        if (stage.endTime && !stage.startTime) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endTime'],
                message: 'End time requires a start time',
            })
        } else if (stage.startTime && stage.endTime && stage.endTime < stage.startTime) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endTime'],
                message: 'End time must be on or after start time',
            })
        }
    })

export const createServiceScheduleInputSchema = z
    .object({
        projectId: z.string().optional(),
        newProject: createProjectInputSchema.optional(),
        serviceTypeId: z.string(),
        startDate: z.date(),
        endDate: z.date(),
        cadenceWeekdays: z.array(z.number().int().min(0).max(6)).min(1),
        serviceNotes: z.string().nullable().optional(),
        serviceWorkerRequirements: z.array(
            z.object({
                workerTypeId: z.string(),
                quantity: z.number().int().positive(),
            }),
        ),
        serviceAssetRequirements: z.array(
            z.object({
                rentalAssetTypeId: z.string(),
                quantity: z.number().int().positive(),
            }),
        ),
        deploy: stageInputSchema.default({workerRequirements: [], assetRequirements: []}),
        pickup: stageInputSchema.default({workerRequirements: [], assetRequirements: []}),
        locations: z.array(locationInputSchema).min(1),
        customerContactId: z.string().nullable().optional(),
        skippedServiceDates: z.array(z.string()).default([]),
        poNumber: z.string().nullable().optional(),
        projectBillingTermsId: z.string().nullable().optional(),
        serviceStartTime: z.string().nullable().optional(),
        serviceEndTime: z.string().nullable().optional(),
        jobsReadyForDispatch: z.boolean().optional().default(true),
        visitsOnly: z.boolean().optional(),
    })
    .superRefine((input, context) => {
        if ((input.projectId == null) === (input.newProject == null)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['projectId'],
                message: 'Order or new order is required',
            })
        }
        if (input.serviceEndTime && !input.serviceStartTime) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['serviceEndTime'],
                message: 'End time requires a start time',
            })
        } else if (
            input.serviceStartTime &&
            input.serviceEndTime &&
            input.serviceEndTime < input.serviceStartTime
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['serviceEndTime'],
                message: 'End time must be on or after start time',
            })
        }
    })

export const createServiceScheduleRequestSchema = createServiceScheduleInputSchema.transform(
    ({
        projectId,
        newProject,
        serviceTypeId,
        startDate,
        endDate,
        cadenceWeekdays,
        serviceNotes,
        serviceWorkerRequirements,
        serviceAssetRequirements,
        deploy,
        pickup,
        locations,
        customerContactId,
        skippedServiceDates,
        poNumber,
        projectBillingTermsId,
        serviceStartTime,
        serviceEndTime,
        jobsReadyForDispatch,
        visitsOnly,
    }) => ({
        ...(visitsOnly ? {visits_only: true} : {}),
        ...(newProject
            ? {new_project: createProjectRequestSchema.parse(newProject)}
            : {project_id: projectId}),
        service_type_id: serviceTypeId,
        start_date: toApiDateString(startDate),
        end_date: toApiDateString(endDate),
        cadence_weekdays: cadenceWeekdays,
        service_notes: serviceNotes?.trim() || null,
        service_worker_requirements: serviceWorkerRequirements.map((requirement) => ({
            worker_type_id: requirement.workerTypeId,
            quantity: requirement.quantity,
        })),
        service_asset_requirements: serviceAssetRequirements.map((requirement) => ({
            rental_asset_type_id: requirement.rentalAssetTypeId,
            quantity: requirement.quantity,
        })),
        deploy: {
            worker_requirements: deploy.workerRequirements.map((requirement) => ({
                worker_type_id: requirement.workerTypeId,
                quantity: requirement.quantity,
            })),
            asset_requirements: deploy.assetRequirements.map((requirement) => ({
                rental_asset_type_id: requirement.rentalAssetTypeId,
                quantity: requirement.quantity,
            })),
            notes: deploy.notes?.trim() || null,
            start_time: deploy.startTime ?? null,
            end_time: deploy.endTime ?? null,
        },
        pickup: {
            worker_requirements: pickup.workerRequirements.map((requirement) => ({
                worker_type_id: requirement.workerTypeId,
                quantity: requirement.quantity,
            })),
            asset_requirements: pickup.assetRequirements.map((requirement) => ({
                rental_asset_type_id: requirement.rentalAssetTypeId,
                quantity: requirement.quantity,
            })),
            notes: pickup.notes?.trim() || null,
            start_time: pickup.startTime ?? null,
            end_time: pickup.endTime ?? null,
        },
        locations: locations.map(toLocationRequest),
        customer_contact_id: customerContactId ?? null,
        skipped_service_dates: skippedServiceDates,
        ...(poNumber !== undefined ? {po_number: poNumber?.trim() || null} : {}),
        ...(projectBillingTermsId !== undefined
            ? {project_billing_terms_id: projectBillingTermsId}
            : {}),
        service_start_time: serviceStartTime ?? null,
        service_end_time: serviceEndTime ?? null,
        jobs_ready_for_dispatch: jobsReadyForDispatch,
    }),
)

export const serviceScheduleChangeInputSchema = z.object({
    endDate: z.date(),
    cadenceWeekdays: z.array(z.number().int().min(0).max(6)).min(1),
    serviceNotes: z.string().nullable().optional(),
    serviceWorkerRequirements: z.array(
        z.object({
            workerTypeId: z.string(),
            quantity: z.number().int().positive(),
        }),
    ),
    serviceAssetRequirements: z.array(
        z.object({
            rentalAssetTypeId: z.string(),
            quantity: z.number().int().positive(),
        }),
    ),
    skippedServiceDates: z.array(z.string()).default([]),
    poNumber: z.string().nullable().optional(),
    projectBillingTermsId: z.string().nullable().optional(),
})

export const serviceScheduleChangeRequestSchema = serviceScheduleChangeInputSchema.transform(
    ({
        endDate,
        cadenceWeekdays,
        serviceNotes,
        serviceWorkerRequirements,
        serviceAssetRequirements,
        skippedServiceDates,
        poNumber,
        projectBillingTermsId,
    }) => ({
        end_date: toApiDateString(endDate),
        cadence_weekdays: cadenceWeekdays,
        service_notes: serviceNotes?.trim() || null,
        service_worker_requirements: serviceWorkerRequirements.map((requirement) => ({
            worker_type_id: requirement.workerTypeId,
            quantity: requirement.quantity,
        })),
        service_asset_requirements: serviceAssetRequirements.map((requirement) => ({
            rental_asset_type_id: requirement.rentalAssetTypeId,
            quantity: requirement.quantity,
        })),
        skipped_service_dates: skippedServiceDates,
        ...(poNumber !== undefined ? {po_number: poNumber?.trim() || null} : {}),
        ...(projectBillingTermsId !== undefined
            ? {project_billing_terms_id: projectBillingTermsId}
            : {}),
    }),
)

export type ScheduleRole = z.infer<typeof scheduleRoleSchema>
export type PreviewOutcome = z.infer<typeof previewOutcomeSchema>
export type UnchangedReason = z.infer<typeof unchangedReasonSchema>
export type ScheduleJobRaw = z.infer<typeof scheduleJobRawSchema>
export type ServiceScheduleRaw = z.infer<typeof serviceScheduleRawSchema>
export type ChangePreviewRaw = z.infer<typeof changePreviewRawSchema>
export type CreateServiceScheduleRequestRaw = z.infer<typeof createServiceScheduleRequestSchema>
export type ServiceScheduleChangeRequestRaw = z.infer<typeof serviceScheduleChangeRequestSchema>
export type ScheduleJob = z.infer<typeof scheduleJobSchema>
export type ServiceSchedule = z.infer<typeof serviceScheduleSchema>
export type ChangePreview = z.infer<typeof changePreviewSchema>
export type PreviewRow = z.infer<typeof previewRowSchema>
export type CreateServiceScheduleInput = z.infer<typeof createServiceScheduleInputSchema>
export type ServiceScheduleChangeInput = z.infer<typeof serviceScheduleChangeInputSchema>
